import { useMemo } from 'react'
import { X, Focus, MessageCircle } from 'lucide-react'
import { KG_NODE_STYLES, DEFAULT_NODE_STYLE } from './kg-constants'
import type { SelectedNodeInfo } from './KgVisualization'

interface Props {
  selectedNodes: SelectedNodeInfo[]
  onDeselectNode: (nodeId: string) => void
  onClearSelection: () => void
  onFocusNode: (nodeId: string) => void
  onAskAboutSelection?: (context: string) => void
}

export function KgSelectionPanel({
  selectedNodes,
  onDeselectNode,
  onClearSelection,
  onFocusNode,
  onAskAboutSelection,
}: Props) {
  // Group nodes by label type
  const grouped = useMemo(() => {
    const map = new Map<string, SelectedNodeInfo[]>()
    for (const node of selectedNodes) {
      const existing = map.get(node.label) || []
      existing.push(node)
      map.set(node.label, existing)
    }
    return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length)
  }, [selectedNodes])

  // Build aggregated insights for element types
  const insights = useMemo(() => {
    const result: { title: string; items: string[] }[] = []

    // Pain points by category
    const painPoints = selectedNodes.filter(n => n.label === 'PainPoint')
    if (painPoints.length > 0) {
      const categories = new Map<string, number>()
      for (const pp of painPoints) {
        const cat = (pp.properties.category as string) || 'Uncategorized'
        categories.set(cat, (categories.get(cat) || 0) + 1)
      }
      result.push({
        title: `Pain Points by Category (${painPoints.length})`,
        items: Array.from(categories.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([cat, count]) => `${cat}: ${count}`),
      })
    }

    // Tools
    const tools = selectedNodes.filter(n => n.label === 'Tool')
    if (tools.length > 0) {
      result.push({
        title: `Tools (${tools.length})`,
        items: tools.map(t => t.caption),
      })
    }

    // Feature matches by product
    const featureMatches = selectedNodes.filter(n => n.label === 'FeatureMatch')
    if (featureMatches.length > 0) {
      const products = new Map<string, number>()
      for (const fm of featureMatches) {
        const prod = (fm.properties.product as string) || 'Unknown'
        products.set(prod, (products.get(prod) || 0) + 1)
      }
      result.push({
        title: `Feature Matches by Product (${featureMatches.length})`,
        items: Array.from(products.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([prod, count]) => `${prod}: ${count}`),
      })
    }

    // Risks
    const risks = selectedNodes.filter(n => n.label === 'Risk')
    if (risks.length > 0) {
      result.push({
        title: `Risks (${risks.length})`,
        items: risks.map(r => r.caption),
      })
    }

    // Value drivers
    const vds = selectedNodes.filter(n => n.label === 'ValueDriver')
    if (vds.length > 0) {
      result.push({
        title: `Value Drivers (${vds.length})`,
        items: vds.map(v => v.caption),
      })
    }

    return result
  }, [selectedNodes])

  // Build a text description of the selection for chat
  const selectionContext = useMemo(() => {
    const lines: string[] = [`Selected ${selectedNodes.length} nodes:`]
    for (const [label, nodes] of grouped) {
      lines.push(`\n${label} (${nodes.length}):`)
      for (const n of nodes.slice(0, 10)) {
        lines.push(`- ${n.caption}`)
      }
      if (nodes.length > 10) {
        lines.push(`  ...and ${nodes.length - 10} more`)
      }
    }
    return lines.join('\n')
  }, [selectedNodes, grouped])

  return (
    <div className="w-80 border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {selectedNodes.length} nodes selected
        </h3>
        <button
          onClick={onClearSelection}
          className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
          title="Clear selection"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Type breakdown */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Type Breakdown
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {grouped.map(([label, nodes]) => {
              const style = KG_NODE_STYLES[label] || DEFAULT_NODE_STYLE
              return (
                <span
                  key={label}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: style.color }}
                  />
                  {nodes.length} {label}
                </span>
              )
            })}
          </div>
        </div>

        {/* Aggregated insights */}
        {insights.length > 0 && (
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Aggregated Insights
            </h4>
            <div className="space-y-3">
              {insights.map((insight) => (
                <div key={insight.title}>
                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {insight.title}
                  </div>
                  <ul className="space-y-0.5">
                    {insight.items.map((item, i) => (
                      <li key={i} className="text-xs text-gray-600 dark:text-gray-400 pl-2">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected node list */}
        <div className="px-4 py-3">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Selected Nodes
          </h4>
          <div className="space-y-1">
            {selectedNodes.map((node) => {
              const style = KG_NODE_STYLES[node.label] || DEFAULT_NODE_STYLE
              return (
                <div
                  key={node.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 group"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: style.color }}
                  />
                  <span className="flex-1 text-xs text-gray-800 dark:text-gray-200 truncate">
                    {node.caption}
                  </span>
                  <button
                    onClick={() => onFocusNode(node.id)}
                    className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400"
                    title="Focus on this node"
                  >
                    <Focus className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => onDeselectNode(node.id)}
                    className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400"
                    title="Remove from selection"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Ask about selection button */}
      {onAskAboutSelection && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => onAskAboutSelection(selectionContext)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            Ask about selection
          </button>
        </div>
      )}
    </div>
  )
}
